import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import { showToast } from "../utils/show_toast"
import { csrfToken } from "../utils/csrf_token"

// Drag-to-reorder day sections. Uses Sortable group "trip-days" so day cards
// never mix with stop rows (group "trip-itinerary" on trip-itinerary-sort).
export default class extends Controller {
  static values = { reorderUrl: String }
  static targets = ["list"]

  connect() {
    if (!this.reorderUrlValue || !this.hasListTarget) return
    if (this.sectionElements().length < 2) return

    this.sortable = Sortable.create(this.listTarget, {
      group: "trip-days",
      handle: ".trip-day-sortable-handle",
      animation: 150,
      ghostClass: "opacity-50",
      draggable: "[data-trip-day-section]",
      onEnd: () => this.persist()
    })
  }

  disconnect() {
    this.sortable?.destroy()
  }

  persist() {
    const orderedIds = this.sectionElements().map((section) => section.dataset.tripDaySection)

    fetch(this.reorderUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken(),
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify({ ordered_ids: orderedIds })
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("reorder failed")
        return response.json()
      })
      .then((payload) => {
        if (payload?.days) this.applyDays(payload.days)
        const itinerary = this.application.getControllerForElementAndIdentifier(
          this.element,
          "trip-itinerary-sort"
        )
        itinerary?.dispatchPoints()
      })
      .catch(() => {
        showToast("Couldn't save the day order", "error")
      })
  }

  applyDays(days) {
    days.forEach((day, index) => {
      const section = this.listTarget.querySelector(`[data-trip-day-section="${day.id}"]`)
      if (!section) return

      const isFirst = index === 0
      section.dataset.tripDayEditorDayOneValue = isFirst ? "true" : "false"

      const title = section.querySelector('[data-trip-day-editor-target="title"]')
      if (title) {
        title.dataset.ordinalLabel = day.ordinal_label
        title.textContent = day.ordinal_label
      }

      const dateDisplay = section.querySelector('[data-trip-day-editor-target="dateDisplay"]')
      const dateInput = section.querySelector('[data-trip-day-editor-target="dateInput"]')
      const dateLink = section.querySelector('[data-trip-day-editor-target="dateLink"]')

      if (dateInput) {
        dateInput.value = day.date || ""
      }

      if (day.date && day.date_display) {
        if (dateDisplay) {
          dateDisplay.textContent = day.date_display
          dateDisplay.classList.remove("hidden")
        }
        dateLink?.classList.add("hidden")
      } else {
        if (dateDisplay) {
          dateDisplay.textContent = ""
          dateDisplay.classList.add("hidden")
        }
        dateLink?.classList.remove("hidden")
      }

      const removeWrap = section.querySelector("[data-trip-day-remove-wrap]")
      if (removeWrap) {
        removeWrap.classList.toggle("hidden", isFirst)
        if (day.remove_hint) {
          const button = removeWrap.querySelector("[data-trip-day-remove]")
          if (button) {
            button.setAttribute("aria-label", day.remove_hint)
          }
          const hint = removeWrap.querySelector("[data-trip-day-remove-hint]")
          if (hint) {
            hint.textContent = day.remove_hint
          }
          const removeForm = removeWrap.querySelector("[data-trip-day-remove-form]")
          if (removeForm) {
            removeForm.dataset.turboConfirm = day.remove_hint
          }
        }
      }

      const editor = this.application.getControllerForElementAndIdentifier(section, "trip-day-editor")
      editor?.snapshot()
    })
  }

  sectionElements() {
    return Array.from(this.listTarget.querySelectorAll(":scope > [data-trip-day-section]"))
  }
}
